import { useState, useRef } from "react";
import { Image, Send, X } from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import StickerPicker from "./StickerPicker";
import GifPicker from "./GifPicker";
import { toast } from "sonner";

export default function PostComposer({ onPosted }) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [sticker, setSticker] = useState(null);
  const [items, setItems] = useState([]); // [{url, media_type}]
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (items.length >= 10) { toast.error("You can add up to 10 photos."); break; }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setItems((prev) => [...prev, { url: data.url, media_type: data.media_type }]);
      }
    } catch (err) { toast.error(apiErr(err)); }
    setUploading(false);
    e.target.value = "";
  };

  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!content.trim() && !items.length && !sticker) return;
    setPosting(true);
    try {
      const { data } = await api.post("/posts", {
        content, sticker, media_items: items,
        media_url: items[0]?.url, media_type: items[0]?.media_type,
      });
      setContent(""); setSticker(null); setItems([]);
      onPosted && onPosted(data);
      toast.success("Shared to the timeline 💛");
    } catch (e) { toast.error(apiErr(e)); }
    setPosting(false);
  };

  if (!user) return null;
  return (
    <div data-testid="post-composer" className="bg-card rounded-2xl border border-border p-4 shadow-sm">
      <div className="flex gap-3">
        <img src={fileUrl(user.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${user.user_id}`} alt="" className="w-10 h-10 rounded-full bg-secondary shrink-0" />
        <div className="flex-1 min-w-0">
          <Textarea data-testid="composer-textarea" value={content} onChange={(e) => setContent(e.target.value)}
            placeholder={`What's on your mind, ${user.name?.split(" ")[0]}?`} rows={2}
            className="border-0 focus-visible:ring-0 resize-none px-0 text-base" />
          {sticker && <div className="text-4xl">{sticker} <button onClick={() => setSticker(null)} className="text-xs text-muted-foreground align-top">✕</button></div>}
          {items.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2" data-testid="composer-media-grid">
              {items.map((m, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-secondary">
                  {m.media_type === "video"
                    ? <video src={fileUrl(m.url)} className="w-full h-full object-cover" />
                    : <img src={fileUrl(m.url)} alt="" className="w-full h-full object-cover" />}
                  <button data-testid={`composer-remove-media-${i}`} onClick={() => removeItem(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
        <div className="flex items-center gap-1">
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={upload} data-testid="composer-file-input" />
          <button onClick={() => fileRef.current?.click()} className="p-2 rounded-full hover:bg-secondary text-sage" title="Add photos/video">
            <Image className="w-5 h-5" />
          </button>
          <StickerPicker onPick={setSticker} />
          <GifPicker onPick={(url) => setItems((prev) => [...prev, { url, media_type: "gif" }])} />
          {uploading && <span className="text-xs text-muted-foreground">Uploading…</span>}
        </div>
        <Button data-testid="composer-post-btn" onClick={submit} disabled={posting || uploading}
          className="bg-copper hover:bg-copper/90 text-white rounded-full px-5">
          <Send className="w-4 h-4 mr-1.5" /> Post
        </Button>
      </div>
    </div>
  );
}
